import React from "react";
import styled from "styled-components";

import { Nullable } from "src/shared/utils/typescript";
import ProfileTag from "src/views/profile/components/ProfileTag";

import { DiscoveryProfile } from "../types";

import WeekendPace from "./WeekendPace";

const hasItems = (items: Nullable<string[]>): items is string[] =>
  Boolean(items && items.length > 0);

export interface ProfileDetailsBreakpoints {
  stackedLayout: number;
  compactDetails: number;
}

const ProfileDetails: React.FC<{
  profile: DiscoveryProfile;
  title: string;
  breakpoints: ProfileDetailsBreakpoints;
  isOwnProfile: boolean;
}> = ({ profile, title, breakpoints, isOwnProfile }) => (
  <Details>
    <DetailsHeader $stackedLayoutBreakpoint={breakpoints.stackedLayout}>
      <DesktopName $stackedLayoutBreakpoint={breakpoints.stackedLayout}>
        {title}
      </DesktopName>
      {profile.weekendPace != null && (
        <WeekendPace profileId={profile.id} value={profile.weekendPace} />
      )}
    </DetailsHeader>

    <DetailSections>
      {(profile.bioPrompt || profile.bio) && (
        <Bio $compactDetailsBreakpoint={breakpoints.compactDetails}>
          {profile.bioPrompt && <BioPrompt>{profile.bioPrompt}</BioPrompt>}
          {profile.bio && <>“{profile.bio}”</>}
        </Bio>
      )}

      {hasItems(profile.skills) && (
        <Section $compactDetailsBreakpoint={breakpoints.compactDetails}>
          <SectionTitle>Skills</SectionTitle>
          <SkillList>
            {profile.skills.map((skill, index) => (
              <Skill key={`${skill}-${index}`}>
                <SkillNumber>{index + 1}</SkillNumber>
                {skill}
              </Skill>
            ))}
          </SkillList>
        </Section>
      )}

      {hasItems(profile.interests) && (
        <Section $compactDetailsBreakpoint={breakpoints.compactDetails}>
          <SectionTitle>Interests</SectionTitle>
          <Chips>
            {profile.interests.map((interest) => (
              <Chip key={interest}>{interest}</Chip>
            ))}
          </Chips>
        </Section>
      )}

      {profile.experienceGoals && (
        <Section $compactDetailsBreakpoint={breakpoints.compactDetails}>
          <SectionTitle>Why I’m here</SectionTitle>
          <Body $compactDetailsBreakpoint={breakpoints.compactDetails}>
            {profile.experienceGoals}
          </Body>
        </Section>
      )}

      {profile.teammateCriteria && (
        <Section $compactDetailsBreakpoint={breakpoints.compactDetails}>
          <SectionTitle>Looking for</SectionTitle>
          <Body $compactDetailsBreakpoint={breakpoints.compactDetails}>
            {profile.teammateCriteria}
          </Body>
        </Section>
      )}

      {hasItems(profile.teammatesClaimIds) && (
        <Section $compactDetailsBreakpoint={breakpoints.compactDetails}>
          <SectionTitle>Teammates</SectionTitle>
          <TeammateCards>
            {profile.teammatesClaimIds.map((claimId) => (
              <ProfileTag
                key={claimId}
                claimId={Number(claimId)}
                authenticatedClaimId={profile.id}
                authenticatedTeammateClaimIds={profile.teammatesClaimIds}
                profileOwnerName={profile.name}
                isOwnProfile={isOwnProfile}
                inlineStatusOnNarrowScreens
              />
            ))}
          </TeammateCards>
        </Section>
      )}
    </DetailSections>
  </Details>
);

const Details = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 16px;
`;

const DetailSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DetailsHeader = styled.div<{ $stackedLayoutBreakpoint: number }>`
  display: flex;
  min-height: 63px;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: ${({ $stackedLayoutBreakpoint }) =>
      $stackedLayoutBreakpoint}px) {
    min-height: auto;
    justify-content: flex-start;
  }
`;

const DesktopName = styled.h2<{ $stackedLayoutBreakpoint: number }>`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 42px;
  line-height: normal;

  @media (max-width: ${({ $stackedLayoutBreakpoint }) =>
      $stackedLayoutBreakpoint}px) {
    display: none;
  }
`;

const Section = styled.section<{ $compactDetailsBreakpoint: number }>`
  display: grid;
  grid-template-columns: 175px minmax(0, 1fr);
  gap: 16px;

  @media (max-width: ${({ $compactDetailsBreakpoint }) =>
      $compactDetailsBreakpoint}px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
`;

const Body = styled.p<{ $compactDetailsBreakpoint: number }>`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-size: 16px;
  line-height: 1.5;

  @media (max-width: ${({ $compactDetailsBreakpoint }) =>
      $compactDetailsBreakpoint}px) {
    font-size: 14px;
  }
`;

const Bio = styled(Body)`
  padding: 16px 24px;
  border-radius: 8px;
  background: #eff8fa;
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};

  @media (max-width: ${({ $compactDetailsBreakpoint }) =>
      $compactDetailsBreakpoint}px) {
    padding: 16px;
  }
`;

const BioPrompt = styled.strong`
  display: block;
  margin-bottom: 8px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TeammateCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 104px);
  align-items: start;
  gap: 12px;

  ${({ theme }) => theme.mediaQueries.tablet`
    grid-template-columns: minmax(0, 1fr);
  `}
`;

const Chip = styled.span`
  padding: 8px 16px;
  border: 2px solid ${({ theme }) => theme.globalConstants.color.brandPrimary};
  border-radius: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-size: 16px;
  line-height: normal;
`;

const SkillList = styled.ol`
  display: flex;
  margin: 0;
  padding: 0;
  flex-direction: column;
  gap: 16px;
  list-style: none;
  max-width: 390px;
`;

const Skill = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-size: 16px;
  line-height: 1.5;
`;

const SkillNumber = styled.span`
  display: inline-flex;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundPrimaryLight};
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
`;

export default ProfileDetails;
